import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAnimatedNavigate } from '../../../hooks/useAnimatedNavigate';
import { useKeycloak } from '@react-keycloak/web';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const API_BASE = '';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

type WizardStep = 1 | 2 | 3 | 4;

interface ReconciliationData {
  reconciliation_id?: string;
  bank_statement_balance: number;
  book_balance: number;
  client_ledgers_total: number;
  outstanding_deposits: number;
  outstanding_checks: number;
  adjusted_bank_balance: number;
  is_balanced: boolean;
  discrepancy_amount: number;
}

const TrustReconciliationWizard: React.FC = () => {
  const { accountId } = useParams();
  const navigate = useAnimatedNavigate();
  const { keycloak } = useKeycloak();

  const [step, setStep] = useState<WizardStep>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1: Period
  const [periodEnd, setPeriodEnd] = useState(new Date().toISOString().split('T')[0]);

  // Step 2: Bank statement
  const [bankBalance, setBankBalance] = useState('');

  // Step 3-4: Results from server
  const [reconData, setReconData] = useState<ReconciliationData | null>(null);
  const [reconId, setReconId] = useState<string | null>(null);

  // Step 4: Review notes
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const getHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${keycloak?.token || ''}`,
  }), [keycloak?.token]);

  // Step 1 → Step 2: Start reconciliation
  const handleStartReconciliation = async () => {
    if (!periodEnd) { setError('Period end date is required.'); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/billing/trust-reconciliations`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          trust_account_id: accountId,
          period_end: periodEnd,
        }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.description || json?.error || 'Failed to start reconciliation');
      setReconId(json.data.reconciliation_id);
      setReconData(json.data);
      setStep(2);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start reconciliation');
    } finally {
      setLoading(false);
    }
  };

  // Step 2 → Step 3: Set bank statement balance
  const handleSetBankBalance = async () => {
    if (!bankBalance || Number(bankBalance) < 0) { setError('Bank statement balance is required.'); return; }
    if (!reconId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/billing/trust-reconciliations/${reconId}/bank-statement`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ bank_statement_balance: Number(bankBalance) }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.description || json?.error || 'Failed to set bank balance');
      setStep(3);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set bank balance');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Calculate balance
  const handleCalculate = async () => {
    if (!reconId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/billing/trust-reconciliations/${reconId}/calculate`, {
        method: 'POST',
        headers: getHeaders(),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.description || json?.error || 'Failed to calculate');
      setReconData(json.data);
      setStep(4);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to calculate');
    } finally {
      setLoading(false);
    }
  };

  // Automatically calculate when entering step 3
  useEffect(() => {
    if (step === 3 && reconId) {
      handleCalculate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Step 4: Submit for review
  const handleSubmitForReview = async () => {
    if (!reconId) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/billing/trust-reconciliations/${reconId}/review`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ notes: reviewNotes || undefined }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.description || json?.error || 'Failed to submit for review');
      navigate(`/billing/trust-accounts/${accountId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit for review');
    } finally {
      setSubmitting(false);
    }
  };

  const stepLabels = ['Select Period', 'Bank Statement', 'Calculate', 'Review & Complete'];

  return (
    <div className="max-w-3xl mx-auto px-6 py-6 space-y-6 lex-tab-panel">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(`/billing/trust-accounts/${accountId}`)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Three-Way Reconciliation</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Bank = Book = Client Ledgers</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="flex items-center gap-2">
        {stepLabels.map((label, i) => {
          const stepNum = (i + 1) as WizardStep;
          const isActive = step === stepNum;
          const isComplete = step > stepNum;
          return (
            <div key={i} className="flex-1">
              <div className={`h-2 rounded-full ${isComplete ? 'bg-teal-500' : isActive ? 'bg-teal-300 dark:bg-teal-600' : 'bg-gray-200 dark:bg-gray-700'}`} />
              <div className={`text-xs mt-1 ${isActive ? 'text-teal-600 dark:text-teal-400 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
                Step {stepNum}: {label}
              </div>
            </div>
          );
        })}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <div key={step} className="lex-tab-panel">
      {/* Step 1: Select Period */}
      {step === 1 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Select Reconciliation Period</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Choose the end date for the reconciliation period. This will capture the book balance and client ledger totals as of this date.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Period End Date</label>
            <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)}
              className="w-full max-w-xs px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
          </div>
          <div className="flex justify-end">
            <button onClick={handleStartReconciliation} disabled={loading}
              className="px-4 py-2 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50">
              {loading ? 'Starting...' : 'Next'}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Bank Statement Balance */}
      {step === 2 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Enter Bank Statement Balance</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Enter the ending balance from the bank statement for this period.
          </p>

          {reconData && (
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 text-sm space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Book Balance (captured):</span>
                <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(reconData.book_balance)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Client Ledger Total (captured):</span>
                <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(reconData.client_ledgers_total)}</span>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bank Statement Balance</label>
            <input type="number" step="0.01" min="0" value={bankBalance} onChange={(e) => setBankBalance(e.target.value)} placeholder="0.00"
              className="w-full max-w-xs px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
          </div>
          <div className="flex justify-between">
            <button onClick={() => setStep(1)} className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
              Back
            </button>
            <button onClick={handleSetBankBalance} disabled={loading}
              className="px-4 py-2 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50">
              {loading ? 'Saving...' : 'Calculate'}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Loading / Calculating */}
      {step === 3 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 flex items-center justify-center py-12">
          <div className="animate-spin h-6 w-6 border-2 border-teal-500 border-t-transparent rounded-full" />
          <span className="ml-2 text-sm text-gray-500">Calculating three-way reconciliation...</span>
        </div>
      )}

      {/* Step 4: Results & Review */}
      {step === 4 && reconData && (
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Three-Way Comparison</h2>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400">Bank Statement Balance:</span>
                <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(reconData.bank_statement_balance)}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400">+ Deposits in Transit:</span>
                <span className="font-medium text-green-600 dark:text-green-400">+{formatCurrency(reconData.outstanding_deposits)}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400">- Outstanding Checks:</span>
                <span className="font-medium text-red-600 dark:text-red-400">-{formatCurrency(reconData.outstanding_checks)}</span>
              </div>
              <div className="flex justify-between py-2 border-b-2 border-gray-300 dark:border-gray-600">
                <span className="font-semibold text-gray-900 dark:text-white">= Adjusted Bank Balance:</span>
                <span className="font-bold text-gray-900 dark:text-white">{formatCurrency(reconData.adjusted_bank_balance)}</span>
              </div>

              <div className="h-2" />

              <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400">Book Balance:</span>
                <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(reconData.book_balance)}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400">Client Ledger Total:</span>
                <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(reconData.client_ledgers_total)}</span>
              </div>
            </div>

            {/* Status */}
            <div className={`flex items-center gap-2 p-3 rounded-lg ${
              reconData.is_balanced
                ? 'bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800'
            }`}>
              {reconData.is_balanced ? (
                <>
                  <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <span className="text-sm font-medium text-green-800 dark:text-green-300">BALANCED - All three values match.</span>
                </>
              ) : (
                <>
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
                  <span className="text-sm font-medium text-red-800 dark:text-red-300">
                    UNBALANCED - Discrepancy of {formatCurrency(Math.abs(reconData.discrepancy_amount))}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Review Notes</h3>
            <textarea
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              rows={3}
              placeholder="Optional notes about this reconciliation..."
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white resize-none"
            />
          </div>

          <div className="flex justify-between">
            <button onClick={() => navigate(`/billing/trust-accounts/${accountId}`)}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
              Cancel
            </button>
            <button onClick={handleSubmitForReview} disabled={submitting}
              className="px-4 py-2 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50">
              {submitting ? 'Submitting...' : 'Complete Reconciliation'}
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default TrustReconciliationWizard;
